/* GrowthOS — Create/Edit Campaign page.
   Same shell as addLead.jsx / addContactsForm.jsx / addDeals.jsx (sticky header,
   cover with identity + readiness card + photo upload, config-driven form sections
   below via campaignForm.jsx) — the fields/required set match Campaign. All the real
   submit / template-filtering / image logic is carried over verbatim from the legacy
   container:
     - create/update via apiServices.createCampaignList / updateCampaignList, then
       singleRowAddUpdateInStore + the campaignDropdownLead/Contact refetches;
     - scheduledDateTime is converted to a UTC ISO string on submit and (on edit)
       campaignStatus is forced to 'Active', exactly as before;
     - "Campaign For" gates the Template dropdown: picking it resets templateId and
       filters the already-loaded templates by module (fetchTemplatesByModule); on
       edit-load the templates are pre-filtered from the record's campaignFor;
     - photo upload reuses the generic *LeadImageCRM Azure-blob helpers with module
       'Campaigns' (the exact module string the legacy HeaderProfileBlock upload used,
       so existing campaign images still resolve). "Remove" only clears local state.
   One correction over the legacy handler: the Template select now also stores
   `templateName` (the legacy only set templateId), so the new gos-dd Select — which
   displays the chosen label from the record — renders the picked template. Same class
   of fix the Deal form made for account/contact display fields. */
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { initValueCampaign } from '../../shared/initials';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore, singleRowAddUpdateInStore } from '../../redux/actions';
import CampaignForm from './campaignForm';
import { validationSchemaCampaign } from './validationSchema';
import { getAllTemplate, getGridDataByModuleKey, getUser } from '../../redux/selector';
import { dropdownPayloadCampaignContact, dropdownPayloadCampaignLead } from '../../shared/constants';

const CL_ICONS = {
  chevL: <path d="M15 18l-6-6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  campaigns: <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14" />,
  calendar: <path d="M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />,
  image: (<><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="M21 16l-5-5L5 20" /></>),
  refresh: <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />,
  trash: <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6" />,
  checkCircle: (<><circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" /></>),
};
const CLIc = ({ name, size = 16, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {CL_ICONS[name] || null}
  </svg>
);

const REQUIRED = [
  { key: 'campaignName', label: 'Campaign Name' },
  { key: 'campaignTypeId', label: 'Type' },
  { key: 'campaignFor', label: 'Campaign For' },
  { key: 'templateId', label: 'Template' },
  { key: 'senderEmail', label: 'Sender' },
  { key: 'scheduledDateTime', label: 'Schedule At' },
];

const AddCampaignForm = () => {
  const stateData = useSelector((state) => state);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gridData = useSelector(getGridDataByModuleKey('campaignList'));
  const userInfo = useSelector(getUser);
  const templates = useSelector(getAllTemplate);
  const storeLoading = isLoadingByKey(stateData, 'owner');

  if (!initValueCampaign.owner) {
    initValueCampaign.owner = userInfo?.user?.display_name;
    initValueCampaign.ownerId = userInfo?.user?.userId;
  }

  const [loading, setLoading] = useState(storeLoading);
  const [formData, setFormData] = useState(initValueCampaign);
  const [errormsg, setErrorMsg] = useState({});
  const [formEdited, setFormEdited] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [templateList, setTemplateList] = useState([]);
  const [imagefile, setImageFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);

  const refs = {
    campaignName: useRef(null),
    campaignTypeId: useRef(null),
    campaignFor: useRef(null),
    templateId: useRef(null),
    senderEmail: useRef(null),
    scheduledDateTime: useRef(null),
  };

  const handleCloseAddForm = () => {
    if (formData.from === 'view') {
      navigate(`/campaigns/viewCampaign?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`);
    } else {
      navigate('/campaigns');
    }
  };

  // filter the already-loaded templates by the selected "Campaign For" module
  const fetchTemplatesByModule = (selectedModule) => {
    const filtered = (templates || []).filter((template) => template.associatedModule === selectedModule);
    setTemplateList(filtered || []);
  };

  const handleUploadImage = async (file) => {
    setImageFile(file);
    setFormEdited(true);
  };

  // show the existing photo when editing
  useEffect(() => {
    if (formData?.id) {
      apiServices
        .getLeadImageCRM(`Campaigns/${formData.id}`)
        .then((url) => setPhotoPreview(url))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.id]);

  const onPhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    handleUploadImage(file);
    e.target.value = '';
  };
  const removePhoto = () => {
    setPhotoPreview(null);
    setImageFile(null);
    setFormEdited(true);
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    setErrorMsg((prev) => ({ ...prev, [name]: undefined }));
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormEdited(true);
  };

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prev) => ({ ...prev, [name]: selectedOption }));
    handleLookupDropDownChange(selectedOption.value, selectedOption, name);
  };

  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.label;
    setErrorMsg((prev) => ({ ...prev, [name]: undefined }));

    if (name === 'ownerId') {
      setFormData((prev) => ({ ...prev, ownerId: selectedId, owner: selectedValue }));
    } else if (name === 'campaignTypeId') {
      setFormData((prev) => ({ ...prev, campaignTypeId: selectedId, campaignType: selectedValue }));
    } else if (name === 'senderEmail') {
      setFormData((prev) => ({ ...prev, senderEmail: selectedId, senderName: selectedValue }));
    } else if (name === 'campaignFor') {
      // store the label (legacy contract — the backend keys templates by this module
      // label), reset the dependent Template, and re-filter templates for the module.
      setFormData((prev) => ({ ...prev, campaignFor: selectedValue, templateId: '', templateName: '' }));
      setSelectedOptions((prev) => ({ ...prev, templateId: null }));
      fetchTemplatesByModule(selectedValue);
    } else if (name === 'templateId') {
      // legacy only stored templateId; also store templateName so the gos-dd Select
      // (which renders the chosen label from the record) shows the pick.
      setFormData((prev) => ({ ...prev, templateId: selectedId, templateName: selectedValue }));
    }
    setFormEdited(true);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      const updatedFormData = {
        ...data,
        scheduledDateTime: data?.scheduledDateTime
          ? new Date(data.scheduledDateTime).toISOString()
          : data.scheduledDateTime,
        ...(data?.type === 'edit' && { campaignStatus: 'Active' }),
      };
      await validationSchemaCampaign.validate(updatedFormData, { abortEarly: false });
      handleFormSubmit(updatedFormData);
    } catch (error) {
      setLoading(false);
      const fieldErrors = {};
      error?.inner?.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
      const errorFields = Object.keys(fieldErrors);
      if (errorFields.length > 0) {
        const errorRef = refs[errorFields[0]];
        if (errorRef?.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorRef.current.focus?.();
        }
      }
      console.error('Validation Error:', fieldErrors);
    }
  };

  const saveUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id.data, { type: imagefile.type });
      await apiServices.uploadLeadImageCRM(newFile, 'Campaigns');
    }
  };
  const editUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id, { type: imagefile.type });
      await apiServices.uploadLeadImageCRM(newFile, 'Campaigns');
    }
  };

  const updateList = async (msg, id) => {
    setLoading(false);
    toast.success(msg, { position: 'top-right' });
    try {
      dispatch(
        singleRowAddUpdateInStore({
          moduleName: 'Campaign',
          id: formData.type === 'add' ? id : formData.id,
          rowIndex: formData.rowIndex,
        })
      );
      dispatch(fetchDataAndUpdateStore({ key: 'campaignDropdownLead', inputData: dropdownPayloadCampaignLead }));
      dispatch(fetchDataAndUpdateStore({ key: 'campaignDropdownContact', inputData: dropdownPayloadCampaignContact }));
      if (formData.from === 'view') {
        navigate(`/campaigns/viewCampaign?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`);
      } else {
        navigate('/campaigns');
      }
    } catch (error) {
      console.error('Failed to fetch and update Campaign:', error);
      toast.error('Something went wrong while updating the list.', { position: 'top-right' });
    }
  };

  const handleFormSubmit = (data) => {
    if (data.type === 'add') {
      apiServices
        .createCampaignList(data)
        .then((response) => {
          if (response.status === 200) {
            const id = response.data;
            saveUploadImage(response);
            updateList('Campaign Created Successfully!', id);
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) errorMessages.push(...response.errors[key]);
            }
            errorMessages.forEach((m) => toast.error(m, { position: 'top-right' }));
          }
        })
        .catch((error) => {
          if (error.response?.data?.errors) {
            const errors = error.response.data.errors;
            Object.keys(errors).forEach((k) => errors[k].forEach((m) => toast.error(` ${m}`, { position: 'top-right' })));
          }
          setLoading(false);
        });
    } else {
      editUploadImage(data.id);
      apiServices
        .updateCampaignList(data)
        .then((response) => {
          if (response.status === 200) {
            updateList('Campaign Updated Successfully!');
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) errorMessages.push(...response.errors[key]);
            }
            errorMessages.forEach((m) => toast.error(m, { position: 'top-right' }));
          }
        })
        .catch((error) => {
          if (error.response?.data?.errors) {
            const errors = error.response.data.errors;
            Object.keys(errors).forEach((k) => errors[k].forEach((m) => toast.error(` ${m}`, { position: 'top-right' })));
          }
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    let updatedFormData = {
      ...formData,
      type: location.pathname.includes('addCampaign') ? 'add' : 'edit',
    };
    if (location.pathname.includes('editCampaign') && location.state) {
      updatedFormData = { ...location.state, type: 'edit' };
    }
    setFormData(updatedFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // on edit-load, pre-filter templates for the record's existing "Campaign For"
  useEffect(() => {
    if (formData?.campaignFor && formData?.type === 'edit') {
      fetchTemplatesByModule(formData.campaignFor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.campaignFor, formData?.type, templates]);

  const isEdit = formData?.type === 'edit';
  const displayName = formData?.campaignName || (isEdit ? 'Campaign' : 'Untitled campaign');
  const idSub = [formData?.campaignType, formData?.campaignFor].filter(Boolean).join(' · ');
  const scheduleChip = formData?.scheduledDateTime
    ? new Date(formData.scheduledDateTime).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;
  const filledReq = REQUIRED.filter((r) => {
    const v = formData?.[r.key];
    return v !== null && v !== undefined && String(v).trim() !== '';
  }).length;
  const pct = Math.round((filledReq / REQUIRED.length) * 100);
  const allReady = filledReq === REQUIRED.length;

  return (
    <div className="addlead_container gos-cl">
      {/* sticky header */}
      <div className="gos-cl-header">
        <button type="button" className="gos-cl-back" onClick={handleCloseAddForm} title="Back to Campaigns">
          <CLIc name="chevL" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-htitle">{isEdit ? 'Edit Campaign' : 'New Campaign'}</div>
          <div className="gos-cl-hsub">
            Fields marked <span className="gos-cl-reqdot">*</span> are required
          </div>
        </div>
        <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCloseAddForm}>Cancel</button>
        <button
          type="button"
          className="gos-btn gos-btn-primary"
          onClick={() => handleSubmit(formData)}
          disabled={loading || (isEdit && !formEdited)}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving…
            </>
          ) : (
            <>
              <CLIc name="check" size={16} /> Save Campaign
            </>
          )}
        </button>
      </div>

      <div className="gos-cl-scroll">
        <div className="gos-cl3-body">
          {/* cover */}
          <div className="gos-cl3-cover">
            <div className="gos-cl-photo-wrap">
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhotoPick} />
              <div
                className={'gos-cl-photo' + (photoPreview ? ' has-img' : '')}
                onClick={photoPreview ? undefined : () => photoInputRef.current?.click()}
              >
                {photoPreview ? (
                  <>
                    <img src={photoPreview} alt="Campaign" />
                    <div className="gos-cl-photo-overlay" onClick={() => photoInputRef.current?.click()}>Change</div>
                  </>
                ) : (
                  <div className="gos-cl-photo-empty">
                    <CLIc name="image" size={24} />
                    <span>Add photo</span>
                  </div>
                )}
              </div>
              {photoPreview ? (
                <div className="gos-cl-photo-actions">
                  <button type="button" className="gos-cl-photo-act" onClick={() => photoInputRef.current?.click()}>
                    <CLIc name="refresh" size={12} /> Change
                  </button>
                  <button type="button" className="gos-cl-photo-act danger" onClick={removePhoto}>
                    <CLIc name="trash" size={12} /> Remove
                  </button>
                </div>
              ) : (
                <div className="gos-cl-photo-hint">JPG or PNG · click to upload</div>
              )}
            </div>

            <div className="gos-cl3-id">
              <div className="gos-cl3-id-eyebrow">{isEdit ? 'Edit Campaign' : 'New Campaign'}</div>
              <div className={'gos-cl3-id-name' + (formData?.campaignName ? '' : ' ph')}>{displayName}</div>
              <div className="gos-cl3-id-sub">{idSub || 'Add campaign details below'}</div>
              <div className="gos-cl3-id-chips">
                <span className="gos-lr-chip"><CLIc name="user" size={13} /> Owner: {formData?.owner || 'Unassigned'}</span>
                {formData?.campaignType ? <span className="gos-lr-chip"><CLIc name="campaigns" size={13} /> {formData.campaignType}</span> : null}
                {scheduleChip ? <span className="gos-lr-chip"><CLIc name="calendar" size={13} /> {scheduleChip}</span> : null}
              </div>
            </div>

            <div className={'gos-cl3-ready' + (allReady ? ' full' : '')}>
              <div className="gos-cl3-ready-top">
                <span className="gos-cl3-ready-label">Campaign readiness</span>
                <span className="gos-cl3-ready-pct">{pct}%</span>
              </div>
              <div className="gos-cl3-bar"><div style={{ width: pct + '%' }} /></div>
              <div className={'gos-cl3-ready-sub' + (allReady ? ' ok' : '')}>
                {allReady ? (
                  <><CLIc name="checkCircle" size={13} /> All required fields complete</>
                ) : (
                  `${filledReq} of ${REQUIRED.length} required fields filled`
                )}
              </div>
            </div>
          </div>

          <CampaignForm
            formData={formData}
            selectedOptions={selectedOptions}
            handleChangeForm={handleChangeForm}
            handleChange={handleChange}
            errormsg={errormsg}
            refs={refs}
            templateList={templateList}
          />
        </div>
      </div>
    </div>
  );
};

export default AddCampaignForm;
